import React, { useCallback } from 'react';

import {
  Box,
  Card,
  Checkbox,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { MapPinIcon } from 'lucide-react';

import { formatDistance } from '@modules/_shared/utils/formatting';
import { useTransactionPresentation } from '@modules/reports/cma-reports/step-transaction/transactionPresentation';
import type {
  MergedTransactionRow,
  TransactionSource,
} from '@modules/reports/cma-reports/step-transaction/types';
import { isToggleSelectionKey } from '@modules/reports/cma-reports/step-transaction/utils/keyboard';
import { useLocale } from '@shared/hooks/locale';

type TransactionInfoProps = {
  presentation: ReturnType<typeof useTransactionPresentation>;
};

export const TransactionInfo = React.memo(
  ({ presentation }: TransactionInfoProps) => {
    const { t } = useLocale();

    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {presentation.propertyTypeLabel}
          {presentation.roomsLabel && ` • ${presentation.roomsLabel}`}
          {presentation.surfaceLabel && ` • ${presentation.surfaceLabel}`}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>
              {presentation.streetLabel && `${presentation.streetLabel}, `}
              {presentation.locality}
              {presentation.postcode && ` (${presentation.postcode})`}
            </span>

            <span>•</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <MapPinIcon
                size={'16px'}
                color={'var(--mui-palette-text-secondary)'}
              />
              <span>{formatDistance(presentation.distance ?? null)}</span>
            </Box>
          </Box>
        </Typography>
        {presentation.formattedDate && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('Sold on')} {presentation.formattedDate}
          </Typography>
        )}
      </Box>
    );
  },
);

export const TransactionInfoSkeleton = () => (
  <Stack spacing={0.5}>
    <Skeleton variant="text" width="60%" height={28} />
    <Skeleton variant="text" width="45%" />
    <Skeleton variant="text" width="35%" />
  </Stack>
);

export const TransactionPriceSkeleton = () => (
  <Stack spacing={0.5}>
    <Skeleton variant="text" width={120} height={28} />
    <Skeleton variant="text" width={90} />
  </Stack>
);

type TransactionPrice = {
  priceLabel?: string | null;
  pricePerSqmLabel?: string | null;
};

export const TransactionPrice = React.memo(
  ({ priceLabel, pricePerSqmLabel }: TransactionPrice) => {
    if (!priceLabel) {
      return null;
    }

    return (
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '1.25rem',
          }}
        >
          {priceLabel}
        </Typography>
        {pricePerSqmLabel && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            {pricePerSqmLabel}
          </Typography>
        )}
      </Box>
    );
  },
);

type TransactionResultItemProps = {
  transaction: MergedTransactionRow;
  selected: boolean;
  onToggle: (id: string, source: TransactionSource) => void;
};

export const TransactionResultItem = React.memo(
  ({ transaction, selected, onToggle }: TransactionResultItemProps) => {
    const presentation = useTransactionPresentation(transaction);

    const handleToggle = useCallback(
      () => onToggle(transaction.transaction_id, transaction.source),
      [onToggle, transaction.transaction_id, transaction.source],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isToggleSelectionKey(e)) {
        e.preventDefault();
        handleToggle();
      }
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      handleToggle();
    };

    const handleCheckboxClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };

    return (
      <Card
        sx={{
          p: 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          border: '1px solid',
          borderColor: 'divider',
        }}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-pressed={selected}
        elevation={0}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            height: '100%',
          }}
        >
          <TransactionInfo presentation={presentation} />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 'auto',
            }}
          >
            <TransactionPrice
              priceLabel={presentation.priceLabel}
              pricePerSqmLabel={presentation.pricePerSqmLabel}
            />

            <Checkbox
              checked={selected}
              onChange={handleCheckboxChange}
              onClick={handleCheckboxClick}
              tabIndex={-1}
              sx={{
                '& .MuiSvgIcon-root': {
                  fontSize: 28,
                },
              }}
              slotProps={{
                input: {
                  'aria-label': `Select ${presentation.propertyTypeLabel} in ${presentation.locality}`,
                },
              }}
            />
          </Box>
        </Box>
      </Card>
    );
  },
);

export default TransactionResultItem;
